import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'sonner';
import type { ComputedFieldDraft } from '../types';
import { chatStore } from './chatStore';
import { useCreateNewChatClient } from './useCreateChatClient';

/**
 * Opens the shared agent drawer from elsewhere in the app — a wrapper over `chatStore`, not a
 * second surface. `openOnField` starts a fresh chat and captures the given field on it, taking the
 * user's unsaved form state rather than the saved schema.
 *
 * The capture is a snapshot: later edits to the form do not reach a chat that is already running,
 * only a draft created from the agent's own offer replaces it. The agent answers about the field as
 * it stood when the user asked.
 */
export const useAgentDrawer = () => {
  const intl = useIntl();
  const { create, isLoading } = useCreateNewChatClient();

  const openOnField = useCallback(
    (draft: ComputedFieldDraft) => {
      create(
        client => {
          const store = chatStore.getState();
          store.setDrawerClient(client);
          store.setIsOpen(true);
          // Last: both calls above clear the draft, since either can mean leaving a chat behind.
          store.setFieldDraft(draft);
        },
        // Without this a failed create is invisible: the button re-enables and no drawer appears.
        () =>
          toast.error(
            intl.formatMessage({ id: 'features.chat.openAgentFailed' })
          )
      );
    },
    [create, intl]
  );

  return { openOnField, isLoading };
};
